import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Output,
  ViewChild,
  HostListener,
} from "@angular/core";

export type PlacementType =
  | "TopLeft"
  | "TopCenter"
  | "TopRight"
  | "BottomLeft"
  | "BottomCenter"
  | "BottomRight"
  | "LeftTop"
  | "LeftCenter"
  | "LeftBottom"
  | "RightTop"
  | "RightCenter"
  | "RightBottom";

@Component({
  selector: "rs-smart-popup",
  templateUrl: "./index.component.html",
  styleUrls: ["./index.component.scss"],
})
export class SmartPopupComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input("placement") _placement: PlacementType = "BottomLeft";
  @Input("offset") _offset: number = 8;
  @Input("autoAdjust") _autoAdjust: "yes" | "no" = "yes";
  @Input("triggerElement") triggerElementRef: HTMLElement | null = null;
  @Input("loading") loading: boolean = false;
  @Input("open") _open: boolean = false;
  @Input("width") _width: number | null = null;
  @Input("height") _height: number | null = null;

  // 解析placement格式
  get placementInfo(): {
    placement: "top" | "left" | "right" | "bottom";
    position: "start" | "center" | "end";
  } {
    const placementMap: {
      [key: string]: {
        placement: "top" | "left" | "right" | "bottom";
        position: "start" | "center" | "end";
      };
    } = {
      TopLeft: { placement: "top", position: "start" },
      TopCenter: { placement: "top", position: "center" },
      TopRight: { placement: "top", position: "end" },
      BottomLeft: { placement: "bottom", position: "start" },
      BottomCenter: { placement: "bottom", position: "center" },
      BottomRight: { placement: "bottom", position: "end" },
      LeftTop: { placement: "left", position: "start" },
      LeftCenter: { placement: "left", position: "center" },
      LeftBottom: { placement: "left", position: "end" },
      RightTop: { placement: "right", position: "start" },
      RightCenter: { placement: "right", position: "center" },
      RightBottom: { placement: "right", position: "end" },
    };
    return (
      placementMap[this._placement] || {
        placement: "bottom",
        position: "start",
      }
    );
  }

  get offset() {
    return this._offset || 8;
  }

  get autoAdjust() {
    return this._autoAdjust === "yes";
  }

  @Output("openChange") openChange = new EventEmitter<boolean>();
  @Output("contentLoad") contentLoad = new EventEmitter<void>();

  @ViewChild("trigger", { static: false })
  internalTriggerElement: ElementRef<HTMLElement>;
  @ViewChild("popup", { static: false })
  popupElement: ElementRef<HTMLDivElement>;

  currentTriggerElement: HTMLElement | null = null;

  constructor(private ref: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.fixedContainerEl = this.getFixedContainer();
    this.fixedContainerEl.append(this.popupElement.nativeElement);
    this.updateTriggerElement();

    // 如果提供了固定尺寸，立即应用
    if (
      this._width !== null &&
      this._height !== null &&
      this.popupElement &&
      this.popupElement.nativeElement
    ) {
      const popupEl = this.popupElement.nativeElement;
      popupEl.style.width = this._width + "px";
      popupEl.style.height = this._height + "px";
      popupEl.style.minWidth = this._width + "px";
      popupEl.style.maxWidth = this._width + "px";
      popupEl.style.minHeight = this._height + "px";
      popupEl.style.maxHeight = this._height + "px";
    }

    // 如果外部传入 open 属性为 true，自动打开
    if (this._open) {
      setTimeout(() => this.open(), 0);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // 处理固定尺寸变化
    if (
      (changes._width || changes._height) &&
      this.popupElement &&
      this.popupElement.nativeElement
    ) {
      const popupEl = this.popupElement.nativeElement;
      if (this._width !== null) {
        popupEl.style.width = this._width + "px";
        popupEl.style.minWidth = this._width + "px";
        popupEl.style.maxWidth = this._width + "px";
      }
      if (this._height !== null) {
        popupEl.style.height = this._height + "px";
        popupEl.style.minHeight = this._height + "px";
        popupEl.style.maxHeight = this._height + "px";
      }
    }

    // 先处理触发元素变化（确保在打开前更新触发元素）
    if (changes.triggerElementRef) {
      const previousTrigger = this.currentTriggerElement;
      this.updateTriggerElement();

      // 如果弹窗已经打开，且触发元素发生了变化，立即更新位置
      if (
        this.opened === "yes" &&
        previousTrigger !== this.currentTriggerElement &&
        this.currentTriggerElement
      ) {
        // 如果提供了固定尺寸，直接重新计算位置（避免位置跳动）
        if (this._width !== null && this._height !== null) {
          // 先隐藏弹窗（positioning = yes）
          this.positioning = "yes";
          this.ref.markForCheck();
          this.ref.detectChanges(); // 立即应用隐藏状态
          
          requestAnimationFrame(() => {
            if (this.currentTriggerElement) {
              const triggerRect = this.currentTriggerElement.getBoundingClientRect();
              const popupSize = {
                width: this._width!,
                height: this._height!,
              };
              const targetInfo = this.placementInfo;
              let finalPlacement = targetInfo.placement;
              const finalPosition = targetInfo.position;

              if (this.autoAdjust) {
                const popupRect: DOMRect = {
                  width: popupSize.width,
                  height: popupSize.height,
                  top: 0,
                  left: 0,
                  right: popupSize.width,
                  bottom: popupSize.height,
                  x: 0,
                  y: 0,
                  toJSON: () => ({}),
                } as DOMRect;
                const optimal = this.calculateOptimalPlacement(
                  triggerRect,
                  popupRect,
                  window.innerWidth,
                  window.innerHeight
                );
                finalPlacement = optimal.placement;
              }

              this.actualPlacement = finalPlacement;
              this.actualPosition = finalPosition;

              const position = this.calculatePositionCoordinates(
                triggerRect,
                finalPlacement,
                finalPosition,
                popupSize
              );

              if (this.popupElement && this.popupElement.nativeElement) {
                const popupEl = this.popupElement.nativeElement;
                popupEl.style.top = position.top;
                popupEl.style.left = position.left;
                popupEl.style.transform = `translate(${position.translateX}, ${position.translateY})`;
                popupEl.offsetHeight; // 强制同步应用样式
                
                // 位置设置完成后，再显示弹窗
                requestAnimationFrame(() => {
                  this.positioning = "no";
                  this.ref.markForCheck();
                });
              }
            }
          });
        } else {
          // 使用新的打开方式更新位置
          this.positioning = "yes";
          this.ref.markForCheck();
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.updatePopupPosition();
              this.positioning = "no";
              this.ref.markForCheck();
            });
          });
        }
      }
    }

    // 处理 open 变化（确保在触发元素更新后处理）
    if (changes._open) {
      if (this._open && this.opened === "no") {
        // 确保触发元素已更新
        this.updateTriggerElement();
        // 使用 setTimeout 确保所有变更都已处理
        setTimeout(() => {
          // 再次确保触发元素已更新
          this.updateTriggerElement();
          this.open();
        }, 0);
      } else if (!this._open && this.opened === "yes") {
        this.close();
      }
    }

    // 当加载完成时，更新位置（如果提供了固定尺寸，不需要更新位置）
    if (
      changes.loading &&
      !changes.loading.currentValue &&
      changes.loading.previousValue
    ) {
      if (this.opened === "yes") {
        // 如果提供了固定尺寸，位置已经计算好了，不需要更新
        if (this._width !== null && this._height !== null) {
          this.contentLoad.emit();
        } else {
          // 如果正在定位，不需要更新位置
          if (this.positioning === "no") {
            requestAnimationFrame(() => {
              this.updatePopupPosition();
              this.contentLoad.emit();
            });
          } else {
            // 如果正在定位，等待定位完成后再触发事件
            const checkPositioning = () => {
              if (this.positioning === "no") {
                this.contentLoad.emit();
              } else {
                setTimeout(checkPositioning, 10);
              }
            };
            checkPositioning();
          }
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.popupElement && this.popupElement.nativeElement) {
      this.popupElement.nativeElement.remove();
    }
    window.removeEventListener("click", this.onClickOutside);
    window.removeEventListener("scroll", this.onWindowScroll, true);
  }

  updateTriggerElement() {
    if (this.triggerElementRef) {
      this.currentTriggerElement = this.triggerElementRef;
    } else if (
      this.internalTriggerElement &&
      this.internalTriggerElement.nativeElement
    ) {
      this.currentTriggerElement = this.internalTriggerElement.nativeElement;
    } else {
      this.currentTriggerElement = null;
    }
  }

  FIXED_CONTAINER_ID = "rs-smart-popup-fixed-container";
  fixedContainerEl: HTMLElement;
  getFixedContainer() {
    let containerElement = document.getElementById(this.FIXED_CONTAINER_ID);
    if (!containerElement) {
      containerElement = document.createElement("div");
      containerElement.setAttribute("id", this.FIXED_CONTAINER_ID);
      document.body.append(containerElement);
    }
    return containerElement;
  }

  opened: "yes" | "no" = "no";
  positioning: "yes" | "no" = "no";
  actualPlacement: "top" | "left" | "right" | "bottom" = "bottom";
  actualPosition: "start" | "center" | "end" = "start";

  open(triggerElement?: HTMLElement) {
    // 如果传入了触发元素，使用它
    if (triggerElement) {
      this.currentTriggerElement = triggerElement;
    } else {
      this.updateTriggerElement();
    }

    if (!this.currentTriggerElement) {
      console.warn("SmartPopupComponent: No trigger element found");
      return;
    }

    // 如果已经打开，且提供了固定尺寸，直接重新计算位置（避免关闭再打开导致的跳动）
    if (this.opened === "yes") {
      if (this._width !== null && this._height !== null) {
        // 有固定尺寸，直接重新计算位置，不关闭弹窗
        // 先隐藏弹窗（positioning = yes）
        this.positioning = "yes";
        this.ref.markForCheck();
        this.ref.detectChanges(); // 立即应用隐藏状态
        
        requestAnimationFrame(() => {
          // 再次确保触发元素已更新
          this.updateTriggerElement();
          if (this.currentTriggerElement) {
            const triggerRect = this.currentTriggerElement.getBoundingClientRect();
            const popupSize = {
              width: this._width!,
              height: this._height!,
            };
            const targetInfo = this.placementInfo;
            let finalPlacement = targetInfo.placement;
            const finalPosition = targetInfo.position;

            if (this.autoAdjust) {
              const popupRect: DOMRect = {
                width: popupSize.width,
                height: popupSize.height,
                top: 0,
                left: 0,
                right: popupSize.width,
                bottom: popupSize.height,
                x: 0,
                y: 0,
                toJSON: () => ({}),
              } as DOMRect;
              const optimal = this.calculateOptimalPlacement(
                triggerRect,
                popupRect,
                window.innerWidth,
                window.innerHeight
              );
              finalPlacement = optimal.placement;
            }

            this.actualPlacement = finalPlacement;
            this.actualPosition = finalPosition;

            const position = this.calculatePositionCoordinates(
              triggerRect,
              finalPlacement,
              finalPosition,
              popupSize
            );

            if (this.popupElement && this.popupElement.nativeElement) {
              const popupEl = this.popupElement.nativeElement;
              popupEl.style.top = position.top;
              popupEl.style.left = position.left;
              popupEl.style.transform = `translate(${position.translateX}, ${position.translateY})`;
              popupEl.offsetHeight; // 强制同步应用样式
              
              // 位置设置完成后，再显示弹窗
              requestAnimationFrame(() => {
                this.positioning = "no";
                this.ref.markForCheck();
              });
            }
          }
        });
        return;
      } else {
        // 没有固定尺寸，先关闭再打开
        this.opened = "no";
        this.positioning = "no";
        this.ref.markForCheck();
        requestAnimationFrame(() => {
          this.updateTriggerElement();
          this.openPopup();
        });
        return;
      }
    }

    // 确保触发元素已更新
    this.updateTriggerElement();
    this.openPopup();
  }

  private openPopup() {
    // 再次确保触发元素已更新
    this.updateTriggerElement();

    if (!this.currentTriggerElement) {
      console.warn(
        "SmartPopupComponent: No trigger element found when opening popup"
      );
      return;
    }

    // 如果提供了固定尺寸，可以在打开前就计算好位置
    if (this._width !== null && this._height !== null) {
      // 先计算位置（不依赖DOM渲染，直接根据triggerElement计算）
      const triggerRect = this.currentTriggerElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const popupSize = {
        width: this._width,
        height: this._height,
      };

      const popupRect: DOMRect = {
        width: popupSize.width,
        height: popupSize.height,
        top: 0,
        left: 0,
        right: popupSize.width,
        bottom: popupSize.height,
        x: 0,
        y: 0,
        toJSON: () => ({}),
      } as DOMRect;

      const targetInfo = this.placementInfo;
      let finalPlacement = targetInfo.placement;
      // 始终使用用户指定的 position，autoAdjust 只调整 placement（方向），不改变对齐方式
      const finalPosition = targetInfo.position;

      if (this.autoAdjust) {
        const optimal = this.calculateOptimalPlacement(
          triggerRect,
          popupRect,
          viewportWidth,
          viewportHeight
        );
        // autoAdjust 只调整 placement（上下左右），保持用户指定的 position（对齐方式）
        finalPlacement = optimal.placement;
        // finalPosition 保持用户指定的值，不改变
      }

      this.actualPlacement = finalPlacement;
      this.actualPosition = finalPosition;

      // 计算位置坐标（不依赖DOM，直接计算）
      const position = this.calculatePositionCoordinates(
        triggerRect,
        finalPlacement,
        finalPosition,
        popupSize
      );

      // 先设置打开状态，但保持隐藏（positioning = yes）
      // 注意：先设置 positioning = yes，再设置 opened = yes，确保弹窗在位置计算完成前始终隐藏
      this.positioning = "yes";
      this.opened = "yes";
      this.openChange.emit(true);
      this.ref.markForCheck();

      // 强制更新视图，确保DOM已更新，但弹窗仍然隐藏（因为 positioning = yes）
      this.ref.detectChanges();

      // 设置位置的函数
      const setPosition = () => {
        if (this.popupElement && this.popupElement.nativeElement) {
          const popupEl = this.popupElement.nativeElement;

          // 立即设置尺寸和位置
          popupEl.style.width = this._width + "px";
          popupEl.style.height = this._height + "px";
          popupEl.style.minWidth = this._width + "px";
          popupEl.style.maxWidth = this._width + "px";
          popupEl.style.minHeight = this._height + "px";
          popupEl.style.maxHeight = this._height + "px";
          popupEl.style.top = position.top;
          popupEl.style.left = position.left;
          popupEl.style.transform = `translate(${position.translateX}, ${position.translateY})`;

          // 强制同步应用样式，确保位置已设置
          popupEl.offsetHeight;

          // 位置设置完成后，再显示弹窗（设置 positioning = no）
          // 使用 requestAnimationFrame 确保样式已应用
          requestAnimationFrame(() => {
            this.positioning = "no";
            this.ref.markForCheck();
          });
        }
      };

      // 如果元素已经存在，立即设置位置
      if (this.popupElement && this.popupElement.nativeElement) {
        setPosition();
      } else {
        // 如果元素还不存在，等待渲染后再设置位置
        requestAnimationFrame(() => {
          if (this.popupElement && this.popupElement.nativeElement) {
            setPosition();
          }
        });
      }
    } else {
      // 没有固定尺寸，需要等待DOM渲染
      this.positioning = "yes";
      this.ref.markForCheck();

      // 立即强制更新视图，确保DOM已更新
      this.ref.detectChanges();

      // 使用 requestAnimationFrame 确保在浏览器渲染之前更新位置
      requestAnimationFrame(() => {
        // 确保元素已渲染并获取尺寸
        if (this.popupElement && this.popupElement.nativeElement) {
          // 强制重新计算布局，确保元素尺寸已计算
          this.popupElement.nativeElement.offsetHeight;

          // 计算并设置位置
          this.updatePopupPosition();

          // 位置设置完成后，移除定位状态，显示弹窗
          this.positioning = "no";
          this.ref.markForCheck();
        }
      });
    }

    window.addEventListener("click", this.onClickOutside);
    window.addEventListener("scroll", this.onWindowScroll, true);
  }

  close() {
    if (this.opened === "no") {
      return;
    }

    this.opened = "no";
    this.positioning = "no";
    this.openChange.emit(false);
    window.removeEventListener("click", this.onClickOutside);
    window.removeEventListener("scroll", this.onWindowScroll, true);
    this.ref.markForCheck();
  }

  toggle() {
    if (this.opened === "yes") {
      this.close();
    } else {
      this.open();
    }
  }

  onClickTrigger(event: MouseEvent) {
    event.stopPropagation();
    this.toggle();
  }

  onClickOutside = (event: MouseEvent) => {
    const target = event.target as Element;
    const isClickOnTrigger =
      this.currentTriggerElement && this.currentTriggerElement.contains(target);
    const isClickOnPopup =
      this.popupElement &&
      this.popupElement.nativeElement &&
      this.popupElement.nativeElement.contains(target);

    if (!isClickOnTrigger && !isClickOnPopup) {
      this.close();
    }
  };

  onWindowScroll = (event: Event) => {
    if (this.opened === "yes") {
      // 检查滚动是否发生在弹窗内部
      const target = event.target as Element;
      const isScrollOnPopup =
        this.popupElement &&
        this.popupElement.nativeElement &&
        this.popupElement.nativeElement.contains(target);

      // 如果滚动发生在弹窗内部，不更新位置（避免不必要的计算）
      // 如果滚动发生在外部，更新位置（因为触发元素位置可能改变）
      if (!isScrollOnPopup) {
        this.updatePopupPosition();
      }
    }
  };

  // 处理弹窗内部的滚轮事件
  onPopupWheel(event: WheelEvent) {
    // 阻止滚轮事件冒泡，避免触发外部滚动处理
    event.stopPropagation();
  }

  // 处理弹窗内部的滚动事件
  onPopupScroll(event: Event) {
    // 阻止滚动事件冒泡，避免触发外部滚动处理
    event.stopPropagation();
  }

  // 计算并设置位置（支持固定尺寸，可在打开前调用）
  private calculateAndSetPosition() {
    if (!this.currentTriggerElement) {
      return;
    }

    const triggerRect = this.currentTriggerElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 获取弹窗尺寸（优先使用固定尺寸）
    const popupSize = {
      width:
        this._width !== null
          ? this._width
          : this.popupElement && this.popupElement.nativeElement
          ? this.popupElement.nativeElement.offsetWidth
          : 300,
      height:
        this._height !== null
          ? this._height
          : this.popupElement && this.popupElement.nativeElement
          ? this.popupElement.nativeElement.offsetHeight
          : 200,
    };

    // 创建虚拟的 popupRect（用于计算）
    const popupRect: DOMRect = {
      width: popupSize.width,
      height: popupSize.height,
      top: 0,
      left: 0,
      right: popupSize.width,
      bottom: popupSize.height,
      x: 0,
      y: 0,
      toJSON: () => ({}),
    } as DOMRect;

    // 获取目标位置信息
    const targetInfo = this.placementInfo;
    let finalPlacement = targetInfo.placement;
    // 始终使用用户指定的 position，autoAdjust 只调整 placement（方向），不改变对齐方式
    const finalPosition = targetInfo.position;

    if (this.autoAdjust) {
      const optimal = this.calculateOptimalPlacement(
        triggerRect,
        popupRect,
        viewportWidth,
        viewportHeight
      );
      // autoAdjust 只调整 placement（上下左右），保持用户指定的 position（对齐方式）
      finalPlacement = optimal.placement;
      // finalPosition 保持用户指定的值，不改变
    }

    this.actualPlacement = finalPlacement;
    this.actualPosition = finalPosition;

    // 立即设置位置（即使元素还没完全渲染，也要先设置位置）
    if (this.popupElement && this.popupElement.nativeElement) {
      this.setPopupPosition(
        triggerRect,
        popupRect,
        finalPlacement,
        finalPosition,
        popupSize
      );
      // 强制应用样式，确保位置立即生效
      this.ref.detectChanges();
    } else {
      // 如果元素还不存在，延迟设置位置
      setTimeout(() => {
        if (this.popupElement && this.popupElement.nativeElement) {
          this.setPopupPosition(
            triggerRect,
            popupRect,
            finalPlacement,
            finalPosition,
            popupSize
          );
          this.ref.detectChanges();
        }
      }, 0);
    }

    this.ref.markForCheck();
  }

  updatePopupPosition() {
    if (
      !this.currentTriggerElement ||
      !this.popupElement ||
      !this.popupElement.nativeElement
    ) {
      return;
    }

    // 如果提供了固定尺寸，使用固定尺寸计算
    if (this._width !== null && this._height !== null) {
      this.calculateAndSetPosition();
      return;
    }

    const triggerRect = this.currentTriggerElement.getBoundingClientRect();
    const popupRect = this.popupElement.nativeElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // 获取目标位置信息
    const targetInfo = this.placementInfo;
    let finalPlacement = targetInfo.placement;
    // 始终使用用户指定的 position，autoAdjust 只调整 placement（方向），不改变对齐方式
    const finalPosition = targetInfo.position;

    if (this.autoAdjust) {
      const optimal = this.calculateOptimalPlacement(
        triggerRect,
        popupRect,
        viewportWidth,
        viewportHeight
      );
      // autoAdjust 只调整 placement（上下左右），保持用户指定的 position（对齐方式）
      finalPlacement = optimal.placement;
      // finalPosition 保持用户指定的值，不改变
    }

    this.actualPlacement = finalPlacement;
    this.actualPosition = finalPosition;
    this.setPopupPosition(
      triggerRect,
      popupRect,
      finalPlacement,
      finalPosition
    );
    this.ref.markForCheck();
  }

  calculateOptimalPlacement(
    triggerRect: DOMRect | ClientRect,
    popupRect: DOMRect | ClientRect,
    viewportWidth: number,
    viewportHeight: number
  ): {
    placement: "top" | "left" | "right" | "bottom";
    position: "start" | "center" | "end";
  } {
    const targetInfo = this.placementInfo;
    const preferred = targetInfo.placement;
    const preferredPosition = targetInfo.position;

    const space = {
      top: triggerRect.top,
      bottom: viewportHeight - triggerRect.bottom,
      left: triggerRect.left,
      right: viewportWidth - triggerRect.right,
    };

    const popupSize = {
      width: popupRect.width || 300, // 默认宽度，如果还没有渲染
      height: popupRect.height || 200, // 默认高度
    };

    // 检查首选位置是否有足够空间
    const hasEnoughSpace = (placement: "top" | "left" | "right" | "bottom") => {
      switch (placement) {
        case "top":
          return space.top >= popupSize.height + this.offset;
        case "bottom":
          return space.bottom >= popupSize.height + this.offset;
        case "left":
          return space.left >= popupSize.width + this.offset;
        case "right":
          return space.right >= popupSize.width + this.offset;
      }
    };

    // 检查指定位置是否有足够空间
    const checkPositionSpace = (
      placement: "top" | "left" | "right" | "bottom",
      position: "start" | "center" | "end"
    ): boolean => {
      if (placement === "top" || placement === "bottom") {
        // 对于上下位置，检查水平对齐空间
        const popupWidth = popupSize.width;
        const spaceLeft = triggerRect.left;
        const spaceRight = viewportWidth - triggerRect.right;

        if (position === "center") {
          return spaceLeft >= popupWidth / 2 && spaceRight >= popupWidth / 2;
        } else if (position === "start") {
          return spaceLeft >= popupWidth;
        } else {
          // end
          return spaceRight >= popupWidth;
        }
      } else {
        // 对于左右位置，检查垂直对齐空间
        const popupHeight = popupSize.height;
        const spaceTop = triggerRect.top;
        const spaceBottom = viewportHeight - triggerRect.bottom;

        if (position === "center") {
          return spaceTop >= popupHeight / 2 && spaceBottom >= popupHeight / 2;
        } else if (position === "start") {
          return spaceTop >= popupHeight;
        } else {
          // end
          return spaceBottom >= popupHeight;
        }
      }
    };

    // 计算位置是否合适（考虑水平/垂直对齐）
    const calculatePosition = (
      placement: "top" | "left" | "right" | "bottom"
    ): "start" | "center" | "end" => {
      if (placement === "top" || placement === "bottom") {
        // 对于上下位置，检查水平对齐
        const popupWidth = popupSize.width;
        const spaceLeft = triggerRect.left;
        const spaceRight = viewportWidth - triggerRect.right;

        // 优先尝试使用用户指定的position
        if (checkPositionSpace(placement, preferredPosition)) {
          return preferredPosition;
        }

        // 如果中心对齐有足够空间，优先使用中心
        if (spaceLeft >= popupWidth / 2 && spaceRight >= popupWidth / 2) {
          return "center";
        }
        // 如果左侧空间不足，使用右对齐
        if (spaceLeft < popupWidth / 2) {
          return "end";
        }
        // 如果右侧空间不足，使用左对齐
        if (spaceRight < popupWidth / 2) {
          return "start";
        }
        return preferredPosition;
      } else {
        // 对于左右位置，检查垂直对齐
        const popupHeight = popupSize.height;
        const spaceTop = triggerRect.top;
        const spaceBottom = viewportHeight - triggerRect.bottom;

        // 优先尝试使用用户指定的position
        if (checkPositionSpace(placement, preferredPosition)) {
          return preferredPosition;
        }

        // 如果中心对齐有足够空间，优先使用中心
        if (spaceTop >= popupHeight / 2 && spaceBottom >= popupHeight / 2) {
          return "center";
        }
        // 如果上方空间不足，使用下对齐
        if (spaceTop < popupHeight / 2) {
          return "end";
        }
        // 如果下方空间不足，使用上对齐
        if (spaceBottom < popupHeight / 2) {
          return "start";
        }
        return preferredPosition;
      }
    };

    // 如果首选位置有足够空间，优先使用用户指定的position
    if (hasEnoughSpace(preferred)) {
      // 即使指定的position空间不足，也尽量保持用户指定的position
      // 边界约束逻辑会在 calculatePositionCoordinates 中处理位置调整
      // 这样可以保持用户期望的对齐方式
      return {
        placement: preferred,
        position: preferredPosition,
      };
    }

    // 根据首选位置选择备选位置
    const alternatives: {
      [key: string]: ("top" | "left" | "right" | "bottom")[];
    } = {
      bottom: ["top", "right", "left"],
      top: ["bottom", "right", "left"],
      right: ["left", "bottom", "top"],
      left: ["right", "bottom", "top"],
    };

    const altList = alternatives[preferred] || [];

    // 尝试找到第一个有足够空间的备选位置
    for (const alt of altList) {
      if (hasEnoughSpace(alt)) {
        return {
          placement: alt,
          position: calculatePosition(alt),
        };
      }
    }

    // 如果都没有足够空间，选择空间最大的位置
    const maxSpace = Math.max(space.top, space.bottom, space.left, space.right);
    let bestPlacement: "top" | "left" | "right" | "bottom";
    if (maxSpace === space.top) bestPlacement = "top";
    else if (maxSpace === space.bottom) bestPlacement = "bottom";
    else if (maxSpace === space.left) bestPlacement = "left";
    else bestPlacement = "right";

    return {
      placement: bestPlacement,
      position: calculatePosition(bestPlacement),
    };
  }

  // 计算位置坐标（纯计算，不依赖DOM，包含边界约束）
  // 注意：弹窗使用 position: fixed，所以坐标应该是相对于视口的，不需要加上滚动偏移
  private calculatePositionCoordinates(
    triggerRect: DOMRect | ClientRect,
    placement: "top" | "left" | "right" | "bottom",
    position: "start" | "center" | "end",
    popupSize: { width: number; height: number }
  ): { top: string; left: string; translateX: string; translateY: string } {
    const triggerWidth = triggerRect.width;
    const triggerHeight = triggerRect.height;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;
    let translateX = "0";
    let translateY = "0";

    switch (placement) {
      case "top":
        // 弹窗在触发元素上方
        top = triggerRect.top - this.offset;
        translateY = "-100%";
        if (position === "start") {
          // 左对齐
          left = triggerRect.left;
          translateX = "0";
        } else if (position === "center") {
          // 居中对齐
          left = triggerRect.left + triggerWidth / 2;
          translateX = "-50%";
        } else {
          // 右对齐（end）
          left = triggerRect.right;
          translateX = "-100%";
        }
        // 边界约束：确保弹窗不超出视口
        const popupTop = top - popupSize.height;
        if (popupTop < 0) {
          top = popupSize.height + 8; // 距离顶部至少8px
          translateY = "0"; // 取消向上偏移
        }
        // 水平边界约束：尽量保持原始对齐方式
        if (position === "start") {
          // 左对齐：如果超出右边界，调整到右边界；如果超出左边界，调整到左边界
          if (left + popupSize.width > viewportWidth) {
            left = viewportWidth - popupSize.width - 8;
          }
          if (left < 8) {
            left = 8;
          }
        } else if (position === "center") {
          // 居中对齐：计算实际左边界
          const actualLeft = left - popupSize.width / 2;
          if (actualLeft < 8) {
            // 左边界不足，尽量保持居中，但调整到不超出左边界
            left = 8 + popupSize.width / 2;
            translateX = "-50%";
          } else if (actualLeft + popupSize.width > viewportWidth - 8) {
            // 右边界不足，尽量保持居中，但调整到不超出右边界
            left = viewportWidth - 8 - popupSize.width / 2;
            translateX = "-50%";
          }
        } else {
          // 右对齐（end）：弹窗右边缘与触发元素右边缘对齐
          // left 是弹窗的右边缘位置（因为 translateX = "-100%"）
          // 弹窗的左边缘在 left - popupSize.width
          const popupLeftEdge = left - popupSize.width;
          
          // 如果弹窗左边缘超出左边界，调整位置但保持右对齐
          if (popupLeftEdge < 8) {
            left = 8 + popupSize.width;
            translateX = "-100%";
          }
          // 如果弹窗右边缘超出右边界，调整位置但保持右对齐
          if (left > viewportWidth - 8) {
            left = viewportWidth - 8;
            translateX = "-100%";
          }
        }
        break;

      case "bottom":
        // 弹窗在触发元素下方
        top = triggerRect.bottom + this.offset;
        if (position === "start") {
          // 左对齐
          left = triggerRect.left;
          translateX = "0";
        } else if (position === "center") {
          // 居中对齐
          left = triggerRect.left + triggerWidth / 2;
          translateX = "-50%";
        } else {
          // 右对齐（end）
          left = triggerRect.right;
          translateX = "-100%";
        }
        // 边界约束：确保弹窗不超出视口
        if (top + popupSize.height > viewportHeight) {
          top = viewportHeight - popupSize.height - 8; // 距离底部至少8px
        }
        // 水平边界约束：尽量保持原始对齐方式
        if (position === "start") {
          // 左对齐：如果超出右边界，调整到右边界；如果超出左边界，调整到左边界
          if (left + popupSize.width > viewportWidth) {
            left = viewportWidth - popupSize.width - 8;
          }
          if (left < 8) {
            left = 8;
          }
        } else if (position === "center") {
          // 居中对齐：计算实际左边界
          const actualLeft = left - popupSize.width / 2;
          if (actualLeft < 8) {
            // 左边界不足，尽量保持居中，但调整到不超出左边界
            left = 8 + popupSize.width / 2;
            translateX = "-50%";
          } else if (actualLeft + popupSize.width > viewportWidth - 8) {
            // 右边界不足，尽量保持居中，但调整到不超出右边界
            left = viewportWidth - 8 - popupSize.width / 2;
            translateX = "-50%";
          }
        } else {
          // 右对齐（end）：弹窗右边缘与触发元素右边缘对齐
          // left 是弹窗的右边缘位置（因为 translateX = "-100%"）
          // 弹窗的左边缘在 left - popupSize.width
          const popupLeftEdge = left - popupSize.width;
          
          // 优先保持与触发元素右对齐，只在必要时调整
          // 如果弹窗右边缘超出右边界，调整位置但保持右对齐
          if (left > viewportWidth - 8) {
            left = viewportWidth - 8;
            translateX = "-100%";
          }
          // 如果弹窗左边缘超出左边界，且弹窗可以完全显示在视口内，才调整位置
          // 这样可以确保在可能的情况下，弹窗始终与触发元素右对齐
          if (popupLeftEdge < 8 && popupSize.width < viewportWidth - 16) {
            // 只有当弹窗可以完全显示在视口内时，才调整位置
            // 否则保持与触发元素右对齐，即使部分超出左边界
            left = 8 + popupSize.width;
            translateX = "-100%";
          }
        }
        break;

      case "left":
        // 弹窗在触发元素左侧
        left = triggerRect.left - this.offset;
        translateX = "-100%";
        if (position === "start") {
          // 上对齐
          top = triggerRect.top;
          translateY = "0";
        } else if (position === "center") {
          // 居中对齐
          top = triggerRect.top + triggerHeight / 2;
          translateY = "-50%";
        } else {
          // 下对齐（end）
          top = triggerRect.bottom;
          translateY = "-100%";
        }
        // 边界约束：确保弹窗不超出视口
        const popupLeftSide = left - popupSize.width;
        if (popupLeftSide < 0) {
          left = 8; // 距离左侧至少8px
          translateX = "0"; // 取消向左偏移
        }
        // 垂直边界约束：尽量保持原始对齐方式
        if (position === "start") {
          // 上对齐：如果超出下边界，调整到下边界；如果超出上边界，调整到上边界
          if (top + popupSize.height > viewportHeight) {
            top = viewportHeight - popupSize.height - 8;
          }
          if (top < 8) {
            top = 8;
          }
        } else if (position === "center") {
          // 居中对齐：计算实际上边界
          const actualTop = top - popupSize.height / 2;
          if (actualTop < 8) {
            // 上边界不足，尽量保持居中，但调整到不超出上边界
            top = 8 + popupSize.height / 2;
            translateY = "-50%";
          } else if (actualTop + popupSize.height > viewportHeight - 8) {
            // 下边界不足，尽量保持居中，但调整到不超出下边界
            top = viewportHeight - 8 - popupSize.height / 2;
            translateY = "-50%";
          }
        } else {
          // 下对齐（end）：如果超出上边界，调整到上边界；如果超出下边界，调整到下边界
          const actualTop = top - popupSize.height;
          if (actualTop < 8) {
            top = 8 + popupSize.height;
            translateY = "-100%";
          }
          if (top > viewportHeight - 8) {
            top = viewportHeight - 8;
            translateY = "-100%";
          }
        }
        break;

      case "right":
        // 弹窗在触发元素右侧
        left = triggerRect.right + this.offset;
        translateX = "0";
        if (position === "start") {
          // 上对齐
          top = triggerRect.top;
          translateY = "0";
        } else if (position === "center") {
          // 居中对齐
          top = triggerRect.top + triggerHeight / 2;
          translateY = "-50%";
        } else {
          // 下对齐（end）
          top = triggerRect.bottom;
          translateY = "-100%";
        }
        // 边界约束：确保弹窗不超出视口
        if (left + popupSize.width > viewportWidth) {
          left = viewportWidth - popupSize.width - 8; // 距离右侧至少8px
        }
        // 垂直边界约束：尽量保持原始对齐方式
        if (position === "start") {
          // 上对齐：如果超出下边界，调整到下边界；如果超出上边界，调整到上边界
          if (top + popupSize.height > viewportHeight) {
            top = viewportHeight - popupSize.height - 8;
          }
          if (top < 8) {
            top = 8;
          }
        } else if (position === "center") {
          // 居中对齐：计算实际上边界
          const actualTop = top - popupSize.height / 2;
          if (actualTop < 8) {
            // 上边界不足，尽量保持居中，但调整到不超出上边界
            top = 8 + popupSize.height / 2;
            translateY = "-50%";
          } else if (actualTop + popupSize.height > viewportHeight - 8) {
            // 下边界不足，尽量保持居中，但调整到不超出下边界
            top = viewportHeight - 8 - popupSize.height / 2;
            translateY = "-50%";
          }
        } else {
          // 下对齐（end）：如果超出上边界，调整到上边界；如果超出下边界，调整到下边界
          const actualTop = top - popupSize.height;
          if (actualTop < 8) {
            top = 8 + popupSize.height;
            translateY = "-100%";
          }
          if (top > viewportHeight - 8) {
            top = viewportHeight - 8;
            translateY = "-100%";
          }
        }
        break;
    }

    return {
      top: top + "px",
      left: left + "px",
      translateX,
      translateY,
    };
  }

  setPopupPosition(
    triggerRect: DOMRect | ClientRect,
    popupRect: DOMRect | ClientRect,
    placement: "top" | "left" | "right" | "bottom",
    position: "start" | "center" | "end",
    popupSize?: { width: number; height: number }
  ) {
    const popupEl =
      this.popupElement && this.popupElement.nativeElement
        ? this.popupElement.nativeElement
        : null;
    if (!popupEl) {
      return; // 如果元素还不存在，无法设置位置
    }

    // 优先使用传入的固定尺寸，否则从DOM获取
    const popupWidth =
      popupSize && popupSize.width !== null && popupSize.width !== undefined
        ? popupSize.width
        : popupRect.width || popupEl.offsetWidth || 300;
    const popupHeight =
      popupSize && popupSize.height !== null && popupSize.height !== undefined
        ? popupSize.height
        : popupRect.height || popupEl.offsetHeight || 200;

    const coordinates = this.calculatePositionCoordinates(
      triggerRect,
      placement,
      position,
      { width: popupWidth, height: popupHeight }
    );

    popupEl.style.top = coordinates.top;
    popupEl.style.left = coordinates.left;
    popupEl.style.transform = `translate(${coordinates.translateX}, ${coordinates.translateY})`;
  }

  @HostListener("window:resize")
  onWindowResize() {
    if (this.opened === "yes") {
      this.updatePopupPosition();
    }
  }
}
